import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { isVenueNotificationFor } from './notificationInboxService';
import app from '../config/firebaseConfig';
import client from '../config/apolloClient';
import { UPDATE_CLUB_FCM_TOKEN } from '../graphql/clubModel/ClubMutation';

let activeVenueEmail = null;
export const setActiveNotificationVenue = email => {
    activeVenueEmail = email?.trim().toLowerCase() || null;
    if (typeof window !== 'undefined' && 'caches' in window) {
        window.caches.open('boozeon-notification-inbox-v1').then(cache =>
            cache.put('/__boozeon_notification_recipient__', new Response(JSON.stringify(activeVenueEmail)))
        ).catch(() => {});
    }
};

let foregroundUnsubscribe = null;
let workerMessageListenerAttached = false;

const dispatchInAppNotification = (payload, storedNotification) => {
    if (!isVenueNotificationFor(payload, activeVenueEmail)) return;
    const title = payload.notification?.title || payload.data?.title || 'BoozeOn update';
    const body = payload.notification?.body || payload.data?.body || '';
    window.dispatchEvent(new CustomEvent('boozeon:notification', {
        detail: { ...storedNotification, title, body, data: payload.data || {} },
    }));
};

const waitForWorkerActivation = worker => {
    if (!worker || worker.state === 'activated') return Promise.resolve();
    return new Promise(resolve => {
        const onStateChange = () => {
            if (worker.state === 'activated' || worker.state === 'redundant') {
                worker.removeEventListener('statechange', onStateChange);
                resolve();
            }
        };
        worker.addEventListener('statechange', onStateChange);
    });
};

const getServiceWorkerRegistration = async () => {
    if (!('serviceWorker' in navigator)) return null;
    const existing = await navigator.serviceWorker.getRegistration('/');
    const registration = existing || await navigator.serviceWorker.register(
        '/service-worker.js',
        { scope: '/' }
    );
    await registration.update();
    await waitForWorkerActivation(registration.installing || registration.waiting);
    return navigator.serviceWorker.ready;
};

const showForegroundNotification = async payload => {
    if (Notification.permission !== 'granted') return;
    dispatchInAppNotification(payload);
};

const attachWorkerMessageListener = () => {
    if (workerMessageListenerAttached) return;
    navigator.serviceWorker.addEventListener('message', event => {
        if (event.data?.type === 'BOOZEON_PUSH' && event.data.payload) {
            dispatchInAppNotification(event.data.payload, event.data.notification);
        }
    });
    workerMessageListenerAttached = true;
};

export const canUseVenueNotifications = async () => (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    await isSupported()
);

export const registerVenueNotifications = async email => {
    setActiveNotificationVenue(email);
    if (!email || Notification.permission !== 'granted') return false;
    if (!(await canUseVenueNotifications())) return false;

    const messaging = getMessaging(app);
    const serviceWorkerRegistration = await getServiceWorkerRegistration();
    attachWorkerMessageListener();
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY?.trim();
    const token = await getToken(messaging, {
        serviceWorkerRegistration,
        ...(vapidKey ? { vapidKey } : {}),
    });
    if (!token) return false;

    await client.mutate({
        mutation: UPDATE_CLUB_FCM_TOKEN,
        variables: { email, fcmToken: token },
    });

    if (!foregroundUnsubscribe) {
        foregroundUnsubscribe = onMessage(messaging, payload => {
            showForegroundNotification(payload).catch(error => {
                console.warn('Foreground notification could not be displayed', error);
            });
        });
    }
    return true;
};

export const requestAndRegisterVenueNotifications = async email => {
    if (!(await canUseVenueNotifications())) return false;
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;
    return registerVenueNotifications(email);
};
